export default class Prop {
  constructor() { }

    /**
   * Return value from object.
   *
   * @example
   *
   *     var obj = {
   *         foo: {
   *             bar: 'hello world!'
   *         }
   *     };
   *
   *     get(obj, 'foo.bar'); // Return 'hello world!'
   *
   * @param {Object} obj Source object.
   * @param {String} keypath Key path to get from object
   * @param {*} defaults Default value to return if key path not found.
   * @returns {*}
   * @author Tomasz Zeludziewicz
   */
    get(obj, keypath, defaults) {

      if (typeof keypath === 'string' && keypath.indexOf('.') === -1) {
        return keypath in obj ? obj[keypath] : defaults;
      }

      var parts = Array.isArray(keypath) ? keypath : keypath.split('.'),
        current = obj,
        i = 0,
        len = parts.length,
        part,
        last = len - 1;

      switch (len) {
        case 0:
          return current;
        case 1:
          return this.hasOwn(current, parts[0]) ? current[parts[0]] : defaults;
        case 2:
          return (this.isObj(current[parts[0]]) && this.hasOwn(current[parts[0]], parts[1])) ?
            current[parts[0]][parts[1]] :
            defaults;
        case 3:
          return (this.isObj(current[parts[0]]) && this.isObj(current[parts[0]][parts[1]]) && this.hasOwn(current[parts[0]][parts[1]], parts[2])) ?
            current[parts[0]][parts[1]][parts[2]] :
            defaults;
      }

      for (; i < len; i++) {
        part = parts[i];

        // If this is not the last element on stack AND it's not object,
        // we can't go further. We will return default value (if provided) or null
        // --
        // We are checking if this is not null, because typeof(null) === 'object'
        if (i !== last && (current[part] === null || typeof current[part] !== 'object')) {
          return defaults;
        } else if (i === last) {
          // Otherwise if this is a last element, we can return its value
          return current[part];
        }

        current = current[part];
      }

      return current;
    }


    /**
   * Sets value for specified key in object.
   *
   * @example
   *
   *     var obj = {
   *         foo: {}
   *     };
   *
   *     // Set value for keypath
   *     // -------------------------------------------------
   *     set('foo.bar', 'hello world!', obj);
   *
   *     // Result
   *     // obj.foo.bar === 'hello world!'
   *
   *     // Set value for keypath that does not exists
   *     // -------------------------------------------------
   *     set('foo.oof.bar', 'hello', obj);
   *
   *     // Result
   *     // throw error because "oof" key does not exists.
   *
   *     // Force setting value for not existing keypath
   *     // -------------------------------------------------
   *     set('foo.oof.bar', 'hello', obj, true);
   *
   *     // Result
   *     // obj.foo.oof.bar === 'hello'
   *
   * @param {Object} obj Source object.
   * @param {String} keypath
   * @param {*} value Value to set.
   * @param {Boolean} force Create keys as objects if not existing.
   * @returns {*}
   */
  set(obj, keypath, value, force) {
    if (arguments.length === 3) {
      force = true;
    }

    if (keypath.indexOf('.') === -1) {
      obj[keypath] = value;
      return value;
    }

    var parts = Array.isArray(keypath) ? keypath : keypath.split('.'),
      current = obj,
      i = 0,
      len = parts.length,
      part,
      last = len - 1,
      type;

    for (; i < len; i++) {
      part = parts[i];
      type = typeof current[part];

      // If this is not the last element on stack AND it's not object or function,
      // we can't go further. We will return default value (if provided) or null.
      // --
      // We are checking if this is not null, because typeof(null) === 'object'
      if (i === last) {
        current[part] = value;
      } else if (current[part] === null || (type !== 'object' && type !== 'function')) {
        if (force) {
          current[part] = {};
        } else {
          throw new Error('Can\'t set value of property "' + part + '". Property is not object.');
        }
      }

      current = current[part];
    }

    return current;
  }

  isObj(value) {
    return (typeof value === 'object' && value !== null);
  }

  hasOwn(object, key) {
    return object.hasOwnProperty(key);
  }
}
